import React, { useEffect, useMemo, useState } from 'react';
import { Heart, MessageCircle, Share2, Sparkles, Loader2, MoreHorizontal } from 'lucide-react';
import api, { absoluteAsset } from '../services/api';

const formatRelative = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'hace unos segundos';
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `hace ${Math.floor(diff / 86400)} d`;
  return d.toLocaleDateString('es-PA', { day: 'numeric', month: 'short', year: 'numeric' });
};

const PostCard = ({ post, liked, onToggleLike }) => {
  const [showFull, setShowFull] = useState(false);
  const img = absoluteAsset(post.image);
  const brandLogo = absoluteAsset(post.brandLogo);
  const desc = post.description || '';
  const isLong = desc.length > 140;
  const visibleDesc = showFull || !isLong ? desc : `${desc.slice(0, 140).trim()}...`;

  return (
    <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <header className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-emerald-100 to-emerald-200 ring-2 ring-emerald-200">
            {brandLogo ? (
              <img src={brandLogo} alt={post.brandName} className="h-full w-full object-contain p-1" />
            ) : (
              <Sparkles className="h-5 w-5 text-emerald-700" />
            )}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-900">
              {post.brandName || 'Catálogo Tzanetatos'}
            </p>
            <p className="text-xs text-slate-500">{formatRelative(post.createdAt)}</p>
          </div>
        </div>
        <button className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Más">
          <MoreHorizontal className="h-5 w-5" />
        </button>
      </header>

      {img && (
        <div className="relative aspect-square w-full overflow-hidden bg-slate-50">
          <img src={img} alt={post.title} className="h-full w-full object-contain" loading="lazy" />
          {post.featured && (
            <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white shadow-md">
              <Sparkles className="h-3 w-3" /> Nuevo
            </span>
          )}
        </div>
      )}

      <div className="px-4 py-3">
        <div className="flex items-center gap-3 text-slate-700">
          <button
            onClick={() => onToggleLike(post.id)}
            className="transition-transform hover:scale-110 active:scale-95"
            aria-label="Me gusta"
          >
            <Heart className={`h-6 w-6 ${liked ? 'fill-red-500 text-red-500' : 'text-slate-700'}`} />
          </button>
          <button className="transition-transform hover:scale-110 active:scale-95" aria-label="Comentar">
            <MessageCircle className="h-6 w-6" />
          </button>
          <button className="transition-transform hover:scale-110 active:scale-95" aria-label="Compartir">
            <Share2 className="h-6 w-6" />
          </button>
        </div>

        <h3 className="mt-3 text-base font-semibold text-slate-900">{post.title}</h3>
        {desc && (
          <p className="mt-1 whitespace-pre-line text-sm text-slate-700">
            {visibleDesc}
            {isLong && (
              <button
                onClick={() => setShowFull(s => !s)}
                className="ml-1 font-medium text-emerald-700 hover:text-emerald-800"
              >
                {showFull ? 'ver menos' : 'ver más'}
              </button>
            )}
          </p>
        )}
      </div>
    </article>
  );
};

const LoNuevo = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [likes, setLikes] = useState(() => {
    try { return JSON.parse(localStorage.getItem('tz_post_likes') || '{}'); } catch (_e) { return {}; }
  });

  useEffect(() => {
    let on = true;
    api.get('/posts')
      .then(({ data }) => { if (on) setPosts(data); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, []);

  const toggleLike = (id) => {
    setLikes(prev => {
      const next = { ...prev, [id]: !prev[id] };
      localStorage.setItem('tz_post_likes', JSON.stringify(next));
      return next;
    });
  };

  const sorted = useMemo(() => {
    return [...posts].sort((a, b) => {
      if (a.featured !== b.featured) return a.featured ? -1 : 1;
      return (b.createdAt || '').localeCompare(a.createdAt || '');
    });
  }, [posts]);

  return (
    <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
      <div className="mb-8 flex items-center gap-3">
        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-700/10 text-emerald-700">
          <Sparkles className="h-6 w-6" />
        </span>
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Lo Nuevo</h1>
          <p className="text-sm text-slate-600">Descubre los últimos productos y novedades del portafolio.</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>
      ) : sorted.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white/60 px-6 py-20 text-center">
          <Sparkles className="mx-auto h-10 w-10 text-slate-400" />
          <h2 className="mt-3 text-lg font-semibold text-slate-800">Aún no hay novedades</h2>
          <p className="mt-1 text-sm text-slate-500">Vuelve pronto para descubrir los nuevos productos del portafolio.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {sorted.map(post => (
            <PostCard key={post.id} post={post} liked={!!likes[post.id]} onToggleLike={toggleLike} />
          ))}
        </div>
      )}
    </div>
  );
};

export default LoNuevo;
